/**
 * Minimal history-mode router. Routes are patterns like `/tafel/:id`.
 * Each route renders into the mount and may return a cleanup function.
 */
import { withTransition } from './ui/transition';
export interface RouteMatch {
  params: Record<string, string>;
  query: URLSearchParams;
}

export type RouteHandler = (mount: HTMLElement, match: RouteMatch) => (() => void) | undefined;

interface Route {
  pattern: RegExp;
  keys: string[];
  handler: RouteHandler;
}

/**
 * Navigate from code that has no router instance (dialogs, buttons).
 * The router listens to popstate and renders the new route.
 */
export function navigate(path: string): void {
  history.pushState(null, '', path);
  dispatchEvent(new PopStateEvent('popstate'));
}

export class Router {
  private readonly routes: Route[] = [];
  private cleanup: (() => void) | null = null;

  public constructor(
    private readonly mount: HTMLElement,
    private readonly notFound: RouteHandler,
  ) {
    window.addEventListener('popstate', () => {
      this.render();
    });
    document.addEventListener('click', (event) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey
      )
        return;
      const a = (event.target as HTMLElement).closest('a[data-link]');
      if (a instanceof HTMLAnchorElement && a.origin === location.origin && a.target === '') {
        event.preventDefault();
        this.go(a.pathname + a.search + a.hash);
      }
    });
  }

  public add(path: string, handler: RouteHandler): this {
    const keys: string[] = [];
    const source = path.replace(/:(\w+)/g, (_, key: string) => {
      keys.push(key);
      return '([^/]+)';
    });
    this.routes.push({ pattern: new RegExp(`^${source}/?$`), keys, handler });
    return this;
  }

  public go(path: string): void {
    history.pushState(null, '', path);
    this.render();
  }

  public render(): void {
    withTransition(() => {
      this.renderNow();
    });
  }

  private renderNow(): void {
    window.scrollTo(0, 0);
    if (this.cleanup !== null) {
      this.cleanup();
      this.cleanup = null;
    }
    this.mount.innerHTML = '';
    const query = new URLSearchParams(location.search);
    for (const route of this.routes) {
      const m = route.pattern.exec(location.pathname);
      if (m === null) continue;
      const params: Record<string, string> = {};
      route.keys.forEach((key, i) => {
        params[key] = decodeURIComponent(m[i + 1] ?? '');
      });
      const result = route.handler(this.mount, { params, query });
      if (typeof result === 'function') this.cleanup = result;
      return;
    }
    const result = this.notFound(this.mount, { params: {}, query });
    if (typeof result === 'function') this.cleanup = result;
  }
}
